import React from "react";
import { Avatar, Tooltip, Badge } from "antd";
import { 
  FaCrown, 
  FaMedal, 
  FaAward, 
  FaUserTie, 
  FaBuilding,
  FaChartLine,
  FaDollarSign,
  FaStar,
  FaTrophy
} from "react-icons/fa";

const TopCustomersGrid = ({ topTenCustomers }) => {
  // Convert Chart.js data to our format
  const customersData =
    topTenCustomers?.labels && topTenCustomers?.datasets?.[0]
      ? topTenCustomers.labels.map((label, idx) => ({
          name: label,
          value: topTenCustomers.datasets[0].data[idx] ?? 0,
          rank: idx + 1,
        }))
      : [];

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <FaCrown style={{ color: '#FFD700', fontSize: 18 }} />;
      case 2:
        return <FaMedal style={{ color: '#C0C0C0', fontSize: 16 }} />;
      case 3:
        return <FaAward style={{ color: '#CD7F32', fontSize: 16 }} />;
      default:
        return <FaStar style={{ color: '#7fffa2', fontSize: 14 }} />;
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1:
        return '#FFD700';
      case 2:
        return '#C0C0C0';
      case 3:
        return '#CD7F32';
      default:
        return '#7fffa2';
    }
  };

  const getCustomerInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatValue = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return `${value.toLocaleString()}`;
  };

  if (!customersData.length) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
          color: "#999",
          fontSize: 14,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <FaUserTie style={{ fontSize: 32, marginBottom: 8, color: '#666' }} />
          <div>Loading customer data...</div>
        </div>
      </div>
    );
  }

  // Split customers into top 3 and remaining 7
  const top3Customers = customersData.slice(0, 3);
  const remainingCustomers = customersData.slice(3, 10);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '12px'
    }}>
      {/* Top 3 Customers - Featured Section */}
      <div style={{
        display: 'flex',
        gap: '8px',
        height: '45%'
      }}>
        {top3Customers.map((customer, index) => (
          <Tooltip
            key={index}
            title={
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {customer.name}
                </div>
                <div>Total Purchases: {formatValue(customer.value)}</div>
                <div>Rank: #{customer.rank}</div>
              </div>
            }
            placement="top"
          >
            <div
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #2a2d3e 0%, #23263a 100%)',
                border: `3px solid ${getRankColor(customer.rank)}`,
                borderRadius: 12,
                padding: 12,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Rank Badge */}
              <div style={{
                position: 'absolute',
                top: 6,
                right: 6,
                background: getRankColor(customer.rank),
                borderRadius: '50%',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                color: '#000'
              }}>
                {customer.rank}
              </div>

              {/* Customer Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Badge
                  count={getRankIcon(customer.rank)}
                  offset={[-3, 3]}
                >
                  <Avatar
                    size={36}
                    style={{
                      background: `linear-gradient(135deg, ${getRankColor(customer.rank)}20, ${getRankColor(customer.rank)}40)`,
                      border: `2px solid ${getRankColor(customer.rank)}`,
                      fontSize: 14,
                      fontWeight: 600,
                      color: getRankColor(customer.rank)
                    }}
                  >
                    {getCustomerInitials(customer.name)}
                  </Avatar>
                </Badge>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#fff',
                    marginBottom: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {customer.name}
                  </div>
                  <div style={{
                    fontSize: 10,
                    color: '#999',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3
                  }}>
                    <FaBuilding style={{ fontSize: 8 }} />
                    #{customer.rank}
                  </div>
                </div>
              </div>

              {/* Purchase Value */}
              <div style={{
                marginTop: 8,
                padding: 6,
                background: 'rgba(127, 255, 162, 0.1)',
                borderRadius: 6,
                border: '1px solid rgba(127, 255, 162, 0.2)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 10,
                    color: '#999'
                  }}>
                    <FaDollarSign style={{ color: '#7fffa2', fontSize: 8 }} />
                    Total
                  </div>
                  <div style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#7fffa2'
                  }}>
                    {formatValue(customer.value)}
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div style={{
                  marginTop: 4,
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  height: 3,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: `linear-gradient(90deg, ${getRankColor(customer.rank)}, #7fffa2)`,
                    height: '100%',
                    width: `${Math.min((customer.value / customersData[0].value) * 100, 100)}%`,
                    borderRadius: 3,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* Trophy for #1 */}
              {customer.rank === 1 && (
                <div style={{
                  position: 'absolute',
                  bottom: 4,
                  left: 4,
                  color: '#FFD700',
                  fontSize: 12
                }}>
                  <FaTrophy />
                </div>
              )}
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Remaining 7 Customers - Scrollable List */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          fontSize: 12,
          fontWeight: 600,
          color: '#999',
          marginBottom: 8,
          textAlign: 'center',
          borderBottom: '1px solid #333',
          paddingBottom: 4
        }}>
          Other Top Performers
        </div>
        <div style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
          {remainingCustomers.map((customer, index) => (
            <Tooltip
              key={index + 3}
              title={
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {customer.name}
                  </div>
                  <div>Total Purchases: {formatValue(customer.value)}</div>
                  <div>Rank: #{customer.rank}</div>
                </div>
              }
              placement="left"
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, #2a2d3e 0%, #23263a 100%)',
                  border: `1px solid ${getRankColor(customer.rank)}`,
                  borderRadius: 8,
                  padding: 8,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  minHeight: 50
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(2px)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Rank Badge */}
                <div style={{
                  background: getRankColor(customer.rank),
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#000',
                  flexShrink: 0
                }}>
                  {customer.rank}
                </div>

                {/* Customer Avatar */}
                <Avatar
                  size={32}
                  style={{
                    background: `linear-gradient(135deg, ${getRankColor(customer.rank)}20, ${getRankColor(customer.rank)}40)`,
                    border: `1px solid ${getRankColor(customer.rank)}`,
                    fontSize: 12,
                    fontWeight: 600,
                    color: getRankColor(customer.rank),
                    flexShrink: 0
                  }}
                >
                  {getCustomerInitials(customer.name)}
                </Avatar>
                
                {/* Customer Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#fff',
                    marginBottom: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {customer.name}
                  </div>
                  <div style={{
                    fontSize: 10,
                    color: '#999'
                  }}>
                    Customer #{customer.rank}
                  </div>
                </div>

                {/* Purchase Value */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexShrink: 0
                }}>
                  <div style={{
                    fontSize: 11,
                    color: '#999',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3
                  }}>
                    <FaDollarSign style={{ color: '#7fffa2', fontSize: 8 }} />
                  </div>
                  <div style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#7fffa2'
                  }}>
                    {formatValue(customer.value)}
                  </div>
                </div>

                {/* Progress Indicator */}
                <div style={{
                  width: 40,
                  height: 4,
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  flexShrink: 0
                }}>
                  <div style={{
                    background: `linear-gradient(90deg, ${getRankColor(customer.rank)}, #7fffa2)`,
                    height: '100%',
                    width: `${Math.min((customer.value / customersData[0].value) * 100, 100)}%`,
                    borderRadius: 2,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopCustomersGrid; 